import React from "react";
import { AiLoadError, useAiRequestGuard, withAiAgentScope } from "./aiAgentRequests";
import { BookOpen, Info, Plus, RefreshCcw, Save, X } from "lucide-react";
import useAiAgentDialog from "./useAiAgentDialog";
import AiAnswerContentNav from "./AiAnswerContentNav";
import { toast } from "react-toastify";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import {
  createAiKnowledgeSource,
  disableAiKnowledgeSource,
  getAiKnowledgeSource,
  getAiKnowledgeSources,
  publishAiKnowledgeSource,
  updateAiKnowledgeSource,
} from "./api/aiAgentApi";
import {
  readAiAgentDraft,
  removeAiAgentDraft,
  writeAiAgentDraft,
} from "./aiAgentDraftStorage";

const SOURCE_LABELS = {
  ManualFaq: "FAQs", ManualText: "Business information", ServiceInfo: "Services",
  Pricing: "Pricing", Policy: "Policies", BusinessInstruction: "Business guidance", SafetyRule: "Safety instructions",
};
const SOURCE_TYPES = Object.keys(SOURCE_LABELS);
const STATUS_HELP = {
  ready: "Available for knowledge answers; response checks still apply.",
  draft: "Saved content. Publish before it can be used for answers.",
  processing: "Publishing is in progress. Refresh to check the result.",
  failed: "Publishing failed. Open the source to review the reason, then retry.",
  disabled: "Excluded from answers. Republish unchanged content, or create a replacement source.",
};
const CONTROL = "mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-slate-50 disabled:text-slate-500";
const BUTTON = "inline-flex h-9 items-center justify-center gap-2 rounded-md border px-3 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 disabled:cursor-not-allowed disabled:opacity-50";

const EMPTY_FORM = {
  id: null,
  title: "",
  sourceType: "ManualText",
  content: "",
};

const KNOWLEDGE_FORM_DRAFT_KEY = "knowledge-form";

function statusClasses(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "ready") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (normalized === "disabled") return "bg-slate-100 text-slate-600 border-slate-200";
  if (normalized === "failed") return "bg-rose-50 text-rose-700 border-rose-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
}

function toForm(item) {
  return {
    id: item?.id || null,
    title: item?.title || "",
    sourceType: item?.sourceType || "ManualText",
    content: item?.content || "",
  };
}

function KnowledgeEditor({ form, onChange, onSave, onClose, saving, loading, error, saveError, onRetry, detail, canManage, dirty }) {
  const dialogRef = useAiAgentDialog(onClose, saving);
  const editable = canManage && detail?.status !== "Disabled";
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-6">
    <section ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={form.id ? editable ? "Edit knowledge source" : "View knowledge source" : "Add knowledge source"}
      className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4">
        <div><h2 className="text-base font-semibold text-slate-900">{form.id ? editable ? "Edit source" : "Source details" : "Add business knowledge"}</h2>
          <p className="mt-1 text-xs text-slate-500">{dirty ? "Unsaved browser draft" : form.id ? "Saved source content" : "Save content first, then publish it separately."}</p></div>
        <button type="button" onClick={onClose} disabled={saving} aria-label="Close knowledge source" className="rounded-md p-2 text-slate-500 hover:bg-slate-200 disabled:opacity-50"><X aria-hidden="true" className="h-4 w-4" /></button>
      </header>
      <div className="overflow-y-auto px-5 py-4">
        {loading ? <p role="status" className="text-sm text-slate-500">Loading source details...</p> : error ? <AiLoadError message={error} onRetry={onRetry} label="source details" /> : <>
          {detail ? <div className="mb-4 space-y-1 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
            <p><span className="font-medium text-slate-800">Saved status: {detail.status || "Unknown"}</span> &middot; {STATUS_HELP[String(detail.status || "").toLowerCase()] || "Review this source before using it."}</p>
            {detail.failureReason ? <p role="alert" className="text-red-700">{detail.failureReason}</p> : null}
            <p>Approximate tokens: {detail.approxTokenCount ?? "Not recorded"}</p>
          </div> : null}
          {saveError ? <p role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{saveError}</p> : null}
          <form id="knowledge-source-form" onSubmit={event => { event.preventDefault(); onSave(); }}>
            <fieldset disabled={saving} className="space-y-4">
              <div className="grid grid-cols-[2fr_1fr] gap-4">
                <label className="block text-sm font-medium text-slate-700">Title
                  <input className={CONTROL} value={form.title} maxLength={160} readOnly={!editable} placeholder="Business hours and contact details" onChange={event => onChange(prev => ({ ...prev, title: event.target.value }))} />
                </label>
                <label className="block text-sm font-medium text-slate-700">Source type
                  <select className={CONTROL} value={form.sourceType} disabled={!editable} onChange={event => onChange(prev => ({ ...prev, sourceType: event.target.value }))}>
                    {SOURCE_TYPES.map(type => <option key={type} value={type}>{SOURCE_LABELS[type]}</option>)}
                  </select>
                </label>
              </div>
              <label className="block text-sm font-medium text-slate-700">Content
                <textarea rows={10} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500" value={form.content} readOnly={!editable}
                  placeholder="Add verified business facts, questions and answers, pricing or policies." onChange={event => onChange(prev => ({ ...prev, content: event.target.value }))} />
              </label>
              {editable ? <p className="flex gap-2 text-xs text-slate-500"><Info aria-hidden="true" className="h-4 w-4 flex-none" />{form.id ? "Saving changes returns this source to Draft and removes it from answer use until you publish it again." : "Creating a draft does not publish knowledge or send customer messages."}</p> : null}
            </fieldset>
          </form>
        </>}
      </div>
      <footer className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-3">
        <button type="button" onClick={onClose} disabled={saving} className={`${BUTTON} border-slate-300 text-slate-700`}>{dirty ? "Keep draft & close" : "Close"}</button>
        {editable && !loading && !error ? <button type="submit" form="knowledge-source-form" disabled={saving} className={`${BUTTON} border-emerald-600 text-emerald-700 hover:bg-emerald-50`}><Save aria-hidden="true" className="h-4 w-4" />{saving ? "Saving..." : form.id ? "Save changes" : "Create draft"}</button> : null}
      </footer>
    </section>
  </div>;
}

function AiKnowledgePage({scope}) {
  const { can, hasAllAccess } = useAuth() || {};
  const canManage =
    Boolean(hasAllAccess) ||
    (typeof can === "function" && can(FK.AI_KNOWLEDGE_MANAGE));

  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(20);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const savingRef = React.useRef(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [detailId, setDetailId] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState("");
  const [editorSaveError, setEditorSaveError] = React.useState("");
  const [action, setAction] = React.useState(null);
  const actionRef = React.useRef(false);
  const [actionError, setActionError] = React.useState("");
  const [loadError, setLoadError] = React.useState("");
  const request = useAiRequestGuard({knowledge: [page, pageSize]});
  const [form, setForm] = React.useState(() => {
    const draft = readAiAgentDraft(KNOWLEDGE_FORM_DRAFT_KEY, scope);
    return draft?.form && typeof draft.form === "object"
      ? { ...EMPTY_FORM, ...draft.form }
      : EMPTY_FORM;
  });
  const [hasUnsavedDraft, setHasUnsavedDraft] = React.useState(() =>
    Boolean(readAiAgentDraft(KNOWLEDGE_FORM_DRAFT_KEY, scope)?.form)
  );

  const load = React.useCallback(async () => {
    const current = request("knowledge");
    setLoading(true); setLoadError(""); setItems([]); setTotal(0);
    try {
      const result = await getAiKnowledgeSources({ page, pageSize });
      if (!current()) return;
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      if (!current()) return;
      const message =
        error?.response?.data?.message || "Failed to load AI knowledge.";
      setLoadError(message); toast.error(message);
    } finally {
      if (current()) setLoading(false);
    }
  }, [page, pageSize, request]);

  React.useEffect(() => {
    load();
  }, [load]);

  const setKnowledgeForm = updater => {
    setEditorSaveError("");
    setForm(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      writeAiAgentDraft(KNOWLEDGE_FORM_DRAFT_KEY, { form: next }, scope);
      setHasUnsavedDraft(true);
      return next;
    });
  };

  const resetForm = () => {
    removeAiAgentDraft(KNOWLEDGE_FORM_DRAFT_KEY, scope);
    setHasUnsavedDraft(false);
    setForm(EMPTY_FORM);
    setDetail(null); setDetailError(""); setEditorSaveError(""); setDetailLoading(false); setEditorOpen(false);
    request("detail");
  };

  const openNew = () => {
    if (!canManage || saving || actionRef.current) return;
    if (!hasUnsavedDraft) { setForm(EMPTY_FORM); setDetail(null); }
    setDetailError(""); setDetailLoading(false); setEditorOpen(true);
  };
  const closeEditor = () => {
    if (savingRef.current) return;
    request("detail"); setDetailLoading(false); setEditorOpen(false);
  };

  const handleSave = async () => {
    if (savingRef.current || actionRef.current || detail?.status === "Disabled") return;
    if (!canManage) {
      toast.error("You do not have permission to manage AI knowledge.");
      return;
    }

    const payload = {
      title: String(form.title || "").trim(),
      sourceType: String(form.sourceType || "ManualText").trim(),
      content: String(form.content || "").trim(),
    };

    if (!payload.title || !payload.content) {
      toast.warn("Title and content are required.");
      return;
    }

    savingRef.current = true; setSaving(true); setActionError(""); setEditorSaveError("");
    try {
      if (form.id) {
        await updateAiKnowledgeSource(form.id, payload);
        toast.success("Knowledge source updated.");
      } else {
        await createAiKnowledgeSource(payload);
        toast.success("Knowledge source created.");
      }
      resetForm();
      await load();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        "Failed to save knowledge source.";
      setEditorSaveError(message); toast.error(message);
    } finally {
      savingRef.current = false; setSaving(false);
    }
  };

  const runSourceAction = async (id, kind, perform, successMessage) => {
    if (!canManage || actionRef.current || savingRef.current || hasUnsavedDraft) return;
    actionRef.current = true; setAction({ id, kind }); setActionError("");
    const current = request("source-action");
    try {
      await perform(id);
      if (!current()) return;
      toast.success(successMessage); await load();
    } catch (error) {
      if (current()) setActionError(error?.response?.data?.message || `Could not ${kind} the source. Try again.`);
    } finally {
      actionRef.current = false; if (current()) setAction(null);
    }
  };
  const handlePublish = id => runSourceAction(id, "publish", publishAiKnowledgeSource, "Knowledge source marked Ready.");
  const handleDisable = id => runSourceAction(id, "disable", disableAiKnowledgeSource, "Knowledge source disabled.");

  const handleEdit = async id => {
    if (savingRef.current || actionRef.current) return;
    if (hasUnsavedDraft) {
      if (form.id === id) { setDetailError(""); setEditorOpen(true); }
      else setActionError("Resume or discard your current browser draft before opening another source.");
      return;
    }
    const current = request("detail");
    setDetailId(id); setForm(toForm({ id })); setDetail(null); setDetailLoading(true); setDetailError(""); setEditorSaveError(""); setEditorOpen(true);
    try {
      const result = await getAiKnowledgeSource(id);
      if (!current()) return;
      setForm(toForm(result)); setDetail(result);
    } catch (error) {
      if (current()) setDetailError(error?.response?.data?.message || "Failed to load source.");
    } finally {
      if (current()) setDetailLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 p-6">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-lg border border-slate-200 bg-white">
        <header className="flex items-start justify-between gap-4 px-5 py-4">
          <div><h1 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><BookOpen aria-hidden="true" className="h-5 w-5 text-emerald-700" />Business knowledge</h1>
            <p className="mt-1 text-sm text-slate-600">Verified business information your assistant can use to answer questions.</p></div>
          {canManage ? <button type="button" onClick={openNew} disabled={saving || Boolean(action)} className={`${BUTTON} flex-none border-emerald-600 text-emerald-700 hover:bg-emerald-50`}><Plus aria-hidden="true" className="h-4 w-4" />{hasUnsavedDraft ? "Resume draft" : "Add source"}</button> : null}
        </header>
        <AiAnswerContentNav current="knowledge" />
        <div className="space-y-4 p-5">
          <div className="space-y-2 text-xs text-slate-500">
            <p className="flex items-start gap-2"><Info aria-hidden="true" className="h-4 w-4 flex-none" />Save a source, then publish it before testing. Publishing may use AI quota.</p>
            <details className="pl-6"><summary className="w-fit cursor-pointer text-slate-600">What goes where?</summary>
              <ul className="mt-2 space-y-1"><li>Business knowledge: verified facts, FAQs and pricing entered as text.</li><li>Configured replies: approved responses to questions or objections during lead collection.</li><li>Fallback &amp; handover: wording for missing answers and situations needing your team.</li><li>Approved media: sendable files, including PDFs. Files do not automatically become knowledge.</li></ul>
            </details>
          </div>
          {hasUnsavedDraft ? <div role="status" className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p>Unsaved knowledge form changes are kept in this browser tab. Resume the draft to continue; saved sources stay unchanged until you save.</p>
            <button type="button" onClick={resetForm} disabled={saving || Boolean(action)} className="flex-none underline underline-offset-2">Discard draft</button>
          </div> : null}
          {actionError ? <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{actionError}</p> : null}
          <AiLoadError message={loadError} onRetry={load} loading={loading} label="knowledge" />
          {!canManage ? <p className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">You can view knowledge sources, but only admins can manage them.</p> : null}
          <section aria-label="Knowledge sources" className="overflow-hidden rounded-lg border border-slate-200">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
              <div><h2 className="text-sm font-medium text-slate-900">Knowledge sources</h2><p className="mt-1 text-xs text-slate-500">{loading ? "Loading sources" : `${total} sources`} &middot; Only Ready sources can be used for answers.</p></div>
              <button type="button" onClick={load} disabled={loading || saving || Boolean(action)} aria-label="Refresh knowledge sources" title="Refresh sources" className={`${BUTTON} w-9 border-slate-300 bg-white px-0 text-slate-600`}><RefreshCcw aria-hidden="true" className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
            </div>
            <div className="overflow-x-auto"><table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium text-slate-500"><tr>
                <th className="px-4 py-3 font-medium">Title / type</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Updated</th><th className="px-4 py-3 font-medium">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">{loading ? <tr><td colSpan={4} className="p-6 text-center text-slate-500">Loading knowledge sources...</td></tr> : !items.length ? <tr><td colSpan={4} className="p-6 text-center text-slate-500">{loadError ? "Knowledge sources are unavailable. Retry the load." : "No knowledge sources yet. Add your first business facts or FAQs."}</td></tr> : items.map(item => {
                const status = String(item.status || "").toLowerCase();
                const busy = saving || Boolean(action);
                return <tr key={item.id} className="align-top">
                  <td className="max-w-sm px-4 py-3"><p className="break-words text-slate-900">{item.title}</p><p className="mt-1 text-xs text-slate-500">{SOURCE_LABELS[item.sourceType] || item.sourceType}</p></td>
                  <td className="max-w-xs px-4 py-3"><span title={STATUS_HELP[status]} className={`inline-flex rounded border px-2 py-0.5 text-xs ${statusClasses(item.status)}`}>{item.status || "Unknown"}</span>{status !== "ready" ? <p className="mt-1 text-xs text-slate-500">{status === "draft" ? "Publish to use for answers." : STATUS_HELP[status] || "Open the source to review its status."}</p> : null}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{item.updatedAtUtc ? new Date(item.updatedAtUtc).toLocaleString() : "Not recorded"}</td>
                  <td className="px-4 py-3"><div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => handleEdit(item.id)} disabled={busy} className={`${BUTTON} border-slate-300 text-slate-700`}>{canManage && status !== "disabled" ? "Edit" : "View"}</button>
                    {canManage ? <>
                      <button type="button" onClick={() => handlePublish(item.id)} disabled={busy || hasUnsavedDraft || status === "ready" || status === "processing"} className={`${BUTTON} border-slate-300 text-slate-700`}>{action?.id === item.id && action.kind === "publish" ? "Publishing..." : "Publish"}</button>
                      <button type="button" onClick={() => handleDisable(item.id)} disabled={busy || hasUnsavedDraft || status === "disabled"} className={`${BUTTON} border-slate-300 text-slate-500`}>{action?.id === item.id && action.kind === "disable" ? "Disabling..." : "Disable"}</button>
                    </> : null}
                  </div></td>
                </tr>;
              })}</tbody>
            </table></div>
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs text-slate-500"><span>Page {page} of {totalPages} ({total} sources)</span><div className="flex gap-2">
              <button type="button" disabled={page <= 1 || loading || saving || Boolean(action)} onClick={() => setPage(p => Math.max(1, p - 1))} className={`${BUTTON} border-slate-300 text-slate-600`}>Prev</button>
              <button type="button" disabled={page >= totalPages || loading || saving || Boolean(action)} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className={`${BUTTON} border-slate-300 text-slate-600`}>Next</button>
            </div></div>
          </section>
        </div>
        {editorOpen ? <KnowledgeEditor form={form} onChange={setKnowledgeForm} onSave={handleSave} onClose={closeEditor} saving={saving}
          loading={detailLoading} error={detailError} saveError={editorSaveError} onRetry={() => handleEdit(detailId)} detail={detail} canManage={canManage} dirty={hasUnsavedDraft} /> : null}
      </div>
    </div>
  );
}

export default withAiAgentScope(AiKnowledgePage);
